import { initializeApp } from 'firebase/app'
import { getDatabase, ref, set, get, push, remove, query, orderByChild, limitToLast, update, serverTimestamp, increment } from 'firebase/database'
import { firebaseConfig } from './analytics'

// Firebase App initialisieren (nur einmal im Projekt)
const app = initializeApp(firebaseConfig)

// Referenz zur Datenbank holen
const database = getDatabase(app)

// Datenstruktur-Typen
export interface MVSCalculation {
  id?: string
  timestamp: number
  fahrzeugTyp: string
  antriebsart: string
  erstzulassung: string
  kmStand: number
  co2Wert: number
  leistung: number
  ergebnis: {
    mvs_alt?: number
    mvs_neu?: number
    differenz?: number
  }
  userAgent?: string
  ip?: string // wird serverseitig anonymisiert
}

export interface UserFeedback {
  id?: string
  timestamp: number
  calculationId?: string
  rating: number // 1-5
  kommentar?: string
}

/**
 * Speichert eine MVS-Berechnung in der Datenbank
 */
export async function saveCalculation(calculation: Omit<MVSCalculation, 'id' | 'timestamp'>) {
  try {
    // Generiere eine neue ID
    const calculationsRef = ref(database, 'calculations')
    const newCalculationRef = push(calculationsRef)
    
    // Füge Zeitstempel hinzu
    const completeCalculation: MVSCalculation = {
      ...calculation,
      timestamp: Date.now()
    }
    
    // Speichere die Berechnung unter der generierten ID
    await set(newCalculationRef, completeCalculation)
    
    return { success: true, id: newCalculationRef.key }
  } catch (error) {
    console.error('Fehler beim Speichern der Berechnung:', error)
    return { success: false, error }
  }
}

/**
 * Holt eine einzelne MVS-Berechnung anhand der ID
 */
export async function getCalculation(id: string) {
  try {
    const calculationRef = ref(database, `calculations/${id}`)
    const snapshot = await get(calculationRef)
    
    if (snapshot.exists()) {
      const data = snapshot.val()
      return { success: true, data: { id, ...data } }
    } else {
      return { success: false, error: 'Berechnung nicht gefunden' }
    }
  } catch (error) {
    console.error('Fehler beim Abrufen der Berechnung:', error)
    return { success: false, error }
  }
}

/**
 * Holt die letzten X Berechnungen
 */
export async function getRecentCalculations(limit = 10) {
  try {
    const calculationsRef = ref(database, 'calculations')
    const calculationsQuery = query(
      calculationsRef,
      orderByChild('timestamp'),
      limitToLast(limit)
    )
    
    const snapshot = await get(calculationsQuery)
    
    if (snapshot.exists()) {
      const calculations: MVSCalculation[] = []
      snapshot.forEach((childSnapshot) => {
        calculations.push({
          id: childSnapshot.key || undefined,
          ...childSnapshot.val()
        })
      })
      
      // Nach Zeitstempel sortieren (neueste zuerst)
      calculations.sort((a, b) => b.timestamp - a.timestamp)
      
      return { success: true, data: calculations }
    } else {
      return { success: true, data: [] }
    }
  } catch (error) {
    console.error('Fehler beim Abrufen der Berechnungen:', error)
    return { success: false, error }
  }
}

/**
 * Inkrementiert den "Gefällt mir"-Zähler in der Firebase Realtime Database
 * Diese Funktion wird für einfache "Daumen hoch"-Klicks verwendet, 
 * während saveFeedback() für detailliertes Feedback verwendet wird.
 */
export async function incrementLikeCounter() {
  try {
    // Atomisches Inkrement vermeiden Race Conditions
    const countersRef = ref(database, 'counters')
    await update(countersRef, { likes: increment(1) })

    // Aktualisierten Wert lesen
    const snapshot = await get(ref(database, 'counters/likes'))
    const newCount = snapshot.exists() ? snapshot.val() : 0

    return {
      success: true,
      count: newCount
    }
  } catch (error) {
    console.error('Fehler beim Inkrementieren des Like-Zählers:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unbekannter Fehler'
    }
  }
}

/**
 * Speichert Benutzerfeedback in der Datenbank
 * HINWEIS: Diese Funktion speichert detailliertes Feedback. 
 * Für einfache "Gefällt mir"-Klicks verwende stattdessen incrementLikeCounter().
 */
export async function saveFeedback(feedback: Omit<UserFeedback, 'id' | 'timestamp'>) {
  try {
    const feedbackRef = ref(database, 'feedback')
    const newFeedbackRef = push(feedbackRef)
    
    const completeFeedback: UserFeedback = {
      ...feedback,
      timestamp: Date.now()
    }
    
    await set(newFeedbackRef, completeFeedback)
    
    return { success: true, id: newFeedbackRef.key }
  } catch (error) {
    console.error('Fehler beim Speichern des Feedbacks:', error)
    return { success: false, error }
  }
}

/**
 * Löscht eine MVS-Berechnung anhand der ID
 */
export async function deleteCalculation(id: string) {
  try {
    const calculationRef = ref(database, `calculations/${id}`)
    await remove(calculationRef)
    return { success: true }
  } catch (error) {
    console.error('Fehler beim Löschen der Berechnung:', error)
    return { success: false, error }
  }
}

/**
 * Ruft Statistiken für das Dashboard ab
 */
export async function getStatistics() {
  try {
    // In einer realen Implementierung würde hier eine komplexere Logik
    // zur Aggregation der Daten stehen. Dies ist eine vereinfachte Version.
    const calculationsRef = ref(database, 'calculations')
    const snapshot = await get(calculationsRef)
    
    if (!snapshot.exists()) {
      return { 
        success: true, 
        data: { 
          totalCalculations: 0,
          avgDifferenz: 0,
          antriebsarten: {},
          fahrzeugTypen: {}
        } 
      }
    }
    
    let totalCalculations = 0
    let sumDifferenz = 0
    const antriebsarten: Record<string, number> = {}
    const fahrzeugTypen: Record<string, number> = {}
    
    snapshot.forEach((childSnapshot) => {
      const calculation = childSnapshot.val() as MVSCalculation
      totalCalculations++
      
      // Differenz aufsummieren
      if (calculation.ergebnis.differenz !== undefined) {
        sumDifferenz += calculation.ergebnis.differenz
      }
      
      // Antriebsarten zählen
      if (calculation.antriebsart) {
        antriebsarten[calculation.antriebsart] = (antriebsarten[calculation.antriebsart] || 0) + 1
      }
      
      // Fahrzeugtypen zählen
      if (calculation.fahrzeugTyp) {
        fahrzeugTypen[calculation.fahrzeugTyp] = (fahrzeugTypen[calculation.fahrzeugTyp] || 0) + 1
      }
    })
    
    const avgDifferenz = totalCalculations > 0 ? sumDifferenz / totalCalculations : 0
    
    return {
      success: true,
      data: {
        totalCalculations,
        avgDifferenz,
        antriebsarten,
        fahrzeugTypen
      }
    }
  } catch (error) {
    console.error('Fehler beim Abrufen der Statistiken:', error)
    return { success: false, error }
  }
}

/**
 * Ruft den aktuellen Like-Zähler aus der Firebase Realtime Database ab
 */
export async function getLikeCounter() {
  try {
    const counterRef = ref(database, 'counters/likes')
    
    // Aktuelle Anzahl lesen
    const snapshot = await get(counterRef)
    let currentCount = 0
    
    if (snapshot.exists()) {
      currentCount = snapshot.val()
    }
    
    return {
      success: true,
      count: currentCount
    }
  } catch (error) {
    console.error('Fehler beim Abrufen des Like-Zählers:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unbekannter Fehler',
      count: 0
    }
  }
}

// Exportiere alle Funktionen als Standard
export default {
  saveCalculation,
  getCalculation,
  getRecentCalculations,
  saveFeedback,
  incrementLikeCounter,
  deleteCalculation,
  getStatistics,
  getLikeCounter
} 
